import { useState, useMemo } from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import CalculatorShell from "@/components/CalculatorShell";
import Field from "@/components/Field";
import ResultStat from "@/components/ResultStat";
import { toNum, monthlyPayment, amortizationByYear } from "@/lib/finance";
import { fmtUSD, fmtPct } from "@/lib/format";
import { STATES, getState } from "@/data/states";
import Seo from "@/components/Seo";
import { breadcrumbJsonLd } from "@/lib/seo";
import EditorBadge from "@/components/EditorBadge";
import { MapPin } from "lucide-react";

// Programmatic SEO page: /calculator/mortgage/:state
// Renders a state-pre-filled mortgage calculator with unique local data.
// Each of the 50 URLs gets its own SEO title, description, schema, and copy.
export default function GeoMortgagePage() {
    const { state: stateCode } = useParams();
    const state = getState(stateCode);

    // Always call hooks in the same order — compute even if state is null
    const [homePrice, setHomePrice] = useState(state?.medianHomePrice ?? 450000);
    const [downPct, setDownPct] = useState(20);
    const [rate, setRate] = useState(6.5);
    const [years, setYears] = useState(30);
    const [propertyTax, setPropertyTax] = useState(state?.propertyTax ?? 1.1);
    const [insurance, setInsurance] = useState(1500);

    const computed = useMemo(() => {
        const hp = toNum(homePrice);
        const dp = toNum(downPct);
        const r = toNum(rate);
        const y = Math.max(1, toNum(years));
        const downAmt = (hp * dp) / 100;
        const loan = Math.max(0, hp - downAmt);
        const pi = monthlyPayment(loan, r, y);
        const tax = (hp * (toNum(propertyTax) / 100)) / 12;
        const ins = toNum(insurance) / 12;
        const mo = pi + tax + ins;
        return { mo, pi, tax, ins, loan, downAmt, schedule: amortizationByYear(loan, r, y), totalPaid: pi * y * 12 };
    }, [homePrice, downPct, rate, years, propertyTax, insurance]);

    if (!state) return <Navigate to="/calculator/mortgage" replace />;

    // Unique SEO content for this state
    const title = `${state.name} Mortgage Calculator — Payment With Local Property Tax`;
    const description = `Calculate a ${state.name} mortgage payment with local property tax (${state.propertyTax}%) and current home price benchmarks. Free, no signup.`;

    const jsonLd = [
        {
            "@context": "https://schema.org",
            "@type": "WebApplication",
            name: title,
            applicationCategory: "FinanceApplication",
            operatingSystem: "Any",
            description,
            areaServed: { "@type": "State", name: state.name },
            offers: { "@type": "Offer", price: "0", priceCurrency: "USD" },
        },
        breadcrumbJsonLd([
            { name: "Home", path: "/" },
            { name: "Mortgage", path: "/calculator/mortgage" },
            { name: state.name, path: `/calculator/mortgage/${state.code}` },
        ]),
    ];

    return (
        <>
            <Seo
                title={`${title} | FinCalc`}
                description={description}
                canonical={`/calculator/mortgage/${state.code}`}
                jsonLd={jsonLd}
                ogSlug="mortgage"
            />

            <div className="container-editorial pt-8 pb-2">
                <nav className="flex items-center gap-2 text-xs text-muted-foreground" aria-label="Breadcrumb">
                    <Link to="/" className="hover:text-primary">Home</Link>
                    <span>/</span>
                    <Link to="/calculator/mortgage" className="hover:text-primary">Mortgage</Link>
                    <span>/</span>
                    <span className="text-primary">{state.name}</span>
                </nav>
            </div>

            <section className="container-editorial pt-4 pb-6 anim-fade-up">
                <div className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.2em] text-accent mb-3">
                    <MapPin className="w-3.5 h-3.5" strokeWidth={1.5} aria-hidden="true" />
                    {state.name} · {state.nickname}
                </div>
                <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-primary leading-[1.05] tracking-tight text-balance">
                    {state.name} Mortgage Calculator
                </h1>
                <p className="mt-4 max-w-2xl text-lg text-foreground/80 leading-relaxed">
                    Buying a home in {state.name}? This calculator is pre-filled with the state&apos;s
                    {" "}<strong>effective property tax rate of {fmtPct(state.propertyTax, 2)}</strong>{" "}
                    and the median home price of <strong>{fmtUSD(state.medianHomePrice, { decimals: 0 })}</strong>.
                    Adjust any number to fit your specific deal.
                </p>
                <div className="mt-5">
                    <EditorBadge />
                </div>
            </section>

            <CalculatorShell
                slug="mortgage"
                hideHeader
                suppressSeo
                explainer={
                    <>
                        <h2>{state.name} mortgage math, in plain terms</h2>
                        <p>
                            Your {state.name} mortgage payment is driven by four variables: home
                            price, down payment, interest rate, and loan term. On top of the base
                            principal &amp; interest (P&amp;I), this calculator adds{" "}
                            <strong>{state.name}&apos;s {fmtPct(state.propertyTax, 2)} effective
                            property tax rate</strong> and a typical homeowners insurance premium
                            so you see the full monthly outlay (PITI).
                        </p>
                        <h3>How {state.name} compares</h3>
                        <ul>
                            <li>
                                <strong>Property tax:</strong> {fmtPct(state.propertyTax, 2)} effective rate.{" "}
                                {state.propertyTax > 1.5 && "Above the US average — noticeably affects monthly payments."}
                                {state.propertyTax >= 0.8 && state.propertyTax <= 1.5 && "Roughly in line with the US national average."}
                                {state.propertyTax < 0.8 && "Below the US average — a real ongoing cost advantage."}
                            </li>
                            <li>
                                <strong>State income tax:</strong>{" "}
                                {state.incomeTax === 0
                                    ? `${state.name} has no state income tax, which boosts your post-tax buying power.`
                                    : `Top marginal rate of ${fmtPct(state.incomeTax, 2)}. Factor this into your take-home pay before stretching on a mortgage.`}
                            </li>
                            <li>
                                <strong>Median home price:</strong>{" "}
                                {fmtUSD(state.medianHomePrice, { decimals: 0 })} as of the most recent
                                Zillow Home Value Index. Individual markets vary significantly.
                            </li>
                        </ul>
                        <h3>{state.name}-specific things to double-check</h3>
                        <p>
                            The property tax rate above is a statewide effective average. County
                            and municipal millage rates vary. Before closing, look up the exact
                            rate for the specific address using the county assessor&apos;s
                            website. For a more conservative estimate, round the tax rate up
                            slightly in your planning — mill rates tend to drift up, not down.
                        </p>
                        <p>
                            This calculator does not include PMI (required for down payments
                            under 20%), HOA fees, or closing costs. For a full picture of your
                            monthly cost, budget an extra 0.5–1.5% of loan for PMI if applicable,
                            plus any community-specific HOA dues.
                        </p>
                    </>
                }
            >
                <div className="grid grid-cols-1 lg:grid-cols-5 gap-8">
                    <div className="lg:col-span-3 bg-card rounded-xl border border-border p-6 sm:p-8 space-y-5">
                        <Field label={`${state.name} home price`} prefix="$" value={homePrice} onChange={setHomePrice} dataTestId="calc-input-price" hint={`State median: ${fmtUSD(state.medianHomePrice, { decimals: 0 })}`} />
                        <div className="grid grid-cols-2 gap-4">
                            <Field label="Down payment" suffix="%" value={downPct} onChange={setDownPct} dataTestId="calc-input-down" />
                            <Field label="Interest rate" suffix="%" value={rate} onChange={setRate} step={0.125} dataTestId="calc-input-rate" />
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                            <Field label="Loan term (years)" value={years} onChange={setYears} dataTestId="calc-input-years" />
                            <Field label={`${state.name} property tax`} suffix="%" value={propertyTax} onChange={setPropertyTax} step={0.05} dataTestId="calc-input-tax" hint={`Default: ${fmtPct(state.propertyTax, 2)} state avg`} />
                        </div>
                        <Field label="Annual home insurance" prefix="$" value={insurance} onChange={setInsurance} dataTestId="calc-input-ins" />
                        <div className="pt-3 flex flex-wrap gap-4 text-xs text-muted-foreground font-mono tabular">
                            <span>Down: {fmtUSD(computed.downAmt)}</span>
                            <span>Loan: {fmtUSD(computed.loan)}</span>
                        </div>
                    </div>
                    <div className="lg:col-span-2">
                        <ResultStat
                            label={`${state.name} monthly payment`}
                            value={computed.mo}
                            format={(n) => fmtUSD(n, { decimals: 0 })}
                            secondary={[
                                { label: "P&I", value: computed.pi, format: (n) => fmtUSD(n), testId: "calc-result-pi" },
                                { label: "Tax + Ins", value: computed.tax + computed.ins, format: (n) => fmtUSD(n), testId: "calc-result-escrow" },
                                { label: "Total paid", value: computed.totalPaid, format: (n) => fmtUSD(n, { decimals: 0 }), testId: "calc-result-totalpaid" },
                                { label: "Total interest", value: computed.totalPaid - computed.loan, format: (n) => fmtUSD(n, { decimals: 0 }), testId: "calc-result-totalinterest" },
                            ]}
                        />
                    </div>
                </div>

                <div className="mt-10 bg-card border border-border rounded-xl p-6">
                    <div className="text-[11px] uppercase tracking-[0.2em] text-accent">Nearby states</div>
                    <h3 className="font-serif text-2xl text-primary mt-1 mb-4">Compare with another state</h3>
                    <div className="flex flex-wrap gap-2">
                        {STATES.filter((s) => s.code !== state.code).slice(0, 12).map((s) => (
                            <Link
                                key={s.code}
                                to={`/calculator/mortgage/${s.code}`}
                                className="text-xs px-3 py-1.5 rounded-full border border-border hover:border-primary/40 text-primary transition-colors"
                                data-testid={`nearby-state-${s.code}`}
                            >
                                {s.name}
                            </Link>
                        ))}
                        <Link
                            to="/calculator/mortgage"
                            className="text-xs px-3 py-1.5 rounded-full bg-primary text-primary-foreground hover:bg-secondary transition-colors"
                        >
                            All states →
                        </Link>
                    </div>
                </div>
            </CalculatorShell>
        </>
    );
}
